import React, { useState, useEffect } from "react";
import axios from "axios";
import { companyCodes } from "../Data/Companycode";
import { departmentCodes } from "../Data/departmentcode";
import { CompanyOptions } from "../Data/Company";
import { DEPARTMENTS } from "../Data/department";
import "./pendingAssetEditor.css";

export const assetFields = [
  ["name", "Registered name"], ["company", "Company"], ["department", "Department"],
  ["category", "Category"], ["type", "Type"], ["subComponent", "Sub-component"],
  ["conditionType", "Condition"], ["assetName", "Asset name"],
  ["assetUserName", "EPF No & Recipient Name"], ["assetModel", "Model"],
  ["serialNumber", "Serial number"], ["assetprice", "Price (Rs)", "number"],
  ["assetUpdateDate", "Register date", "date"], ["assetTransferDate", "Transfer date", "date"],
  ["supplierName", "Supplier name"], ["invoiceDetails", "Invoice number"],
  ["purchaseDate", "Purchase date", "date"], ["warranty", "Warranty"],
  ["specialNote", "Special note", "textarea"],
];
const componentFields = ["CPU", "Moniter", "Mouse", "Keyboard"].flatMap(prefix =>
  [["assetName", "name"], ["assetModel", "model"], ["price", "price"]].map(([suffix, title]) =>
    [prefix + suffix, (prefix === "Moniter" ? "Monitor" : prefix) + " " + title]));
export const editableFields = [...assetFields, ...componentFields].map(([key]) => key).filter(key => key !== "name");

export function updateAssetField(asset, key, value) {
  if (key === "name") return asset;
  const next = { ...asset, [key]: value };
  if (["company", "department", "serialNumber"].includes(key) && value !== asset[key]) {
    const suffix = String(next.serialNumber || "").trim().slice(-8) ||
      new Date().toISOString().slice(2, 10).replace(/-/g, "") + String(Math.floor(Math.random() * 100)).padStart(2, "0");
    next.trackingId = (companyCodes[next.company] || "XX") + "-" +
      (departmentCodes[next.department] || "XX") + "-" + suffix;
  }
  if (key === "category" && value !== asset.category) { next.type = ""; next.subComponent = ""; }
  if (key === "type" && value !== asset.type) next.subComponent = "";
  return next;
}
export function pendingAssetFormData(asset) {
  const data = new FormData();
  for (const key of [...editableFields, "trackingId", "assetImageURL", "invoiceFileURL"]) {
    if (asset[key] !== undefined && asset[key] !== null) data.append(key, asset[key]);
  }
  if (asset.computerComponents !== undefined) data.append("computerComponents",
    JSON.stringify(asset.computerComponents));
  for (const key of ["assetImage", "invoiceFile"]) if (asset[key] instanceof Blob) data.append(key, asset[key]);
  return data;
}

export default function PendingAssetEditor({ asset, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => ({ ...asset }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [optionsStatus, setOptionsStatus] = useState("loading");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setOptionsStatus("loading");
    axios.get("/api/categories/getCategory").then(response => {
      if (!Array.isArray(response.data)) throw new Error("Invalid categories");
      if (active) { setCategories(response.data); setOptionsStatus("ready"); }
    }).catch(() => { if (active) setOptionsStatus("error"); });
    return () => { active = false; };
  }, [retry]);
  const unique = values => [...new Set(values.filter(Boolean))];
  const selectOptions = {
    company: CompanyOptions.map(option => option.value),
    department: DEPARTMENTS.map(option => option.value),
    category: unique(categories.map(row => row.category)),
    type: unique(categories.filter(row => row.category === draft.category).map(row => row.types)),
    subComponent: unique(categories.filter(row => row.category === draft.category && row.types === draft.type).map(row => row.subComponent)),
    conditionType: ["Brand New", "Used"],
  };
  const change = (key, value) => setDraft(prev => updateAssetField(prev, key, value));
  const renderField = ([key, title, type = "text"]) => (
    <label key={key}>{title}
      {type === "textarea" ? <textarea value={draft[key] || ""} onChange={e => change(key, e.target.value)} /> :
        selectOptions[key] ? <select value={draft[key] || ""}
          required={["company", "category", "conditionType"].includes(key)}
          disabled={["category", "type", "subComponent"].includes(key) && optionsStatus !== "ready"}
          onChange={e => change(key, e.target.value)}>
          <option value="">Select {title.toLowerCase()}</option>
          {draft[key] && !selectOptions[key].includes(draft[key]) && <option value={draft[key]}>{draft[key]} (current)</option>}
          {selectOptions[key].map(value => <option key={value} value={value}>{value}</option>)}
        </select> :
        <input readOnly={key === "name"} type={type} step={type === "number" ? "any" : undefined}
          min={type === "number" ? "0" : undefined}
          required={["name", "company", "category"].includes(key)}
          value={draft[key] || ""} onChange={e => change(key, e.target.value)} />}
    </label>
  );
  const save = async e => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const response = await axios.put(
        "/api/PendingAssetRegisterDetails/updatePendingAsset/" + asset._id,
        pendingAssetFormData(draft));
      onSaved(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Unable to save changes. Please try again.");
    } finally { setBusy(false); }
  };
  return <div className="pending-edit-overlay">
    <form className="pending-edit-dialog" role="dialog" aria-modal="true" aria-labelledby="pending-edit-title" onSubmit={save}>
      <h3 id="pending-edit-title">Edit pending asset</h3>
      <fieldset disabled={busy}>
        <label>Tracking ID (automatically generated)
          <input value={draft.trackingId || ""} readOnly />
        </label>
        {optionsStatus === "loading" && <p role="status">Loading category options...</p>}
        {optionsStatus === "error" && <p role="alert">Unable to load category options. <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button></p>}
        <div className="pending-edit-grid">{assetFields.map(renderField)}</div>
        <label>Computer components
          {draft.computerComponents && typeof draft.computerComponents === "object" ?
            Object.entries(draft.computerComponents).map(([key, value]) => <label key={key}>{key}
              <input value={String(value ?? "")} onChange={e => change("computerComponents", { ...draft.computerComponents, [key]: e.target.value })} />
            </label>) :
            <input value={draft.computerComponents || ""} onChange={e => change("computerComponents", e.target.value)} />}
        </label>
        <details><summary>Computer set details</summary><div className="pending-edit-grid">{componentFields.map(renderField)}</div></details>
        <div className="pending-edit-grid">
          {[["assetImage", "assetImageURL", "Asset image", "image/*"], ["invoiceFile", "invoiceFileURL", "Invoice attachment", "image/*,application/pdf"]].map(([fileKey, urlKey, title, accept]) =>
            <div key={fileKey}><label>{title}<input type="file" accept={accept} onChange={e => change(fileKey, e.target.files[0] || null)} /></label>
              {draft[urlKey] && <a href={draft[urlKey]} target="_blank" rel="noopener noreferrer">View current attachment</a>}
              {(draft[urlKey] || draft[fileKey]) && <button type="button" onClick={() => setDraft(prev => ({ ...prev, [urlKey]: "", [fileKey]: null }))}>Remove attachment</button>}
            </div>)}
        </div>
      </fieldset>
      {error && <p role="alert" className="pending-edit-error">{error}</p>}
      <div className="pending-edit-footer"><button type="button" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="submit" disabled={busy}>{busy ? "Saving..." : "Save changes"}</button></div>
    </form>
  </div>;
}
